<template>

  <!-- Create Project button -->
  <div class="mb-5 text-center">
    <div class="col-lg-6 mx-auto">
      <p class="lead">Create and edit your own video editing projects!</p>
      <div class="d-grid gap-2 d-sm-flex justify-content-sm-center">
        <button type="button" class="btn btn-primary btn-lg px-4 gap-3" @click="showNewProjectModal">Create Project</button>
      </div>
    </div>
  </div>

  <!-- Project thumbnails -->
  <div class="row gy-2 gx-2 mb-2">
    <div v-for="project in thumbnailedProjects" :key="project.id" class="col-sm-12 col-md-6 col-lg-3 project-card" style="position: relative;">

      <!-- Project name badge -->
      <span class="project-badge badge">{{ project.name }}</span>

      <!-- Context menu for project -->
      <div class="btn-group dropstart context-menu" title="Project Menu">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="dropdown-toggle bi bi-three-dots-vertical" data-bs-toggle="dropdown" aria-expanded="false" viewBox="0 0 16 16">
          <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
        </svg>
        <ul class="dropdown-menu small-dropdown">
          <li><a class="dropdown-item" href="#" @click="launchEditor(project)">Launch Editor</a></li>
          <template v-if="$showApiLinks">
            <li><a class="dropdown-item" :href="project.url" target="_blank" rel="noopener">View in API</a></li>
            <li><hr class="dropdown-divider"></li>
          </template>
          <li><a class="dropdown-item" href="#" @click="editProjectClick(project)">Edit</a></li>
          <li><a class="dropdown-item" href="#" @click="showDeletePrompt(project)">Delete</a></li>
        </ul>
      </div>

      <!-- Project profile label -->
      <div class="profile-label">
        {{ project.width }}x{{ project.height}}@{{ (project.fps_num / project.fps_den).toFixed(0) }}fps
      </div>

      <!-- Project thumbnail image -->
      <img @click="launchEditor(project)" class="img-fluid img-thumbnail project-thumbnail" :title="project.name" :src="project.thumbnail"/>
    </div>
  </div>

  <!-- Loading spinner -->
  <div v-if="show_spinner" class="md-5 p-4 text-center project-list-spinner">
    <div class="spinner-border project-spinner" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- New Project Modal -->
  <div class="modal fade" ref="newProjectModal" tabindex="-1" aria-labelledby="newModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 v-if="!editing" class="modal-title" id="newModalLabel">Create Project</h5>
          <h5 v-if="editing" class="modal-title" id="newModalLabel">Edit Project</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">

          <div class="mb-3">
            <label class="form-label">Project Name:</label>
            <input v-model="project_name" type="text" class="form-control">
          </div>

          <div class="mb-3">
            <select v-model="selected_profile" @change="profileChanged" class="form-select" aria-label="Choose a profile...">
              <option value="" selected>Choose a profile...</option>
              <option v-for="profile in profiles" :key="profile.id" :value="profile.id">
                {{ profile.width }}x{{ profile.height }} @ {{ profile.fps }}fps</option>
            </select>
          </div>

          <label class="form-label">Size:</label>
          <div class="input-group mb-3">
            <input v-model="project_width" type="text" class="form-control" placeholder="Width">
            <span class="input-group-text">x</span>
            <input v-model="project_height" type="text" class="form-control" placeholder="Height">
          </div>

          <label class="form-label">Framerate / FPS:</label>
          <div class="input-group mb-3">
            <input v-model="project_fps_num" type="text" class="form-control" placeholder="FPS Numerator">
            <span class="input-group-text">/</span>
            <input v-model="project_fps_den" type="text" class="form-control" placeholder="FPS Denominator">
          </div>

          <div v-if="!editing" class="mb-3">
            <div class="form-check form-switch">
              <input class="form-check-input"
                     type="checkbox"
                     id="loadArchiveSwitch"
                     v-model="load_archive_enabled"
                     @change="archiveSwitchChanged">
              <label class="form-check-label" for="loadArchiveSwitch">Load Existing Project</label>
            </div>
            <div v-if="load_archive_enabled" class="mt-2">
              <input ref="projectArchiveInput"
                     type="file"
                     class="d-none"
                     accept=".zip,.osp"
                     @change="onArchiveSelected">
              <div class="d-flex align-items-center flex-wrap gap-2">
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="triggerArchiveBrowse">
                  Browse Project
                </button>
                <span v-if="project_archive_file" class="text-muted archive-file-name">{{ project_archive_file.name }}</span>
                <span v-else class="text-muted">No project selected</span>
                <button v-if="project_archive_file"
                        type="button"
                        class="btn btn-link btn-sm text-decoration-none"
                        @click="clearArchiveSelection">
                  Clear
                </button>
              </div>
              <div class="form-text">
                Optional: load *.zip of an existing OpenShot project.
              </div>
            </div>
          </div>

        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" ref="CloseProjectModal" data-bs-dismiss="modal">Cancel</button>
          <button v-if="!editing"
                  type="button"
                  class="btn btn-primary"
                  :disabled="creating_project"
                  @click="createProjectClick">
            <span v-if="creating_project" class="spinner-border spinner-border-sm align-middle me-2 create-btn-spinner" role="status" aria-hidden="true"></span>
            Create Project
          </button>
          <button v-if="editing" type="button" class="btn btn-primary" @click="updateProjectClick">Save Project</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Project Modal -->
  <div class="modal fade" ref="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="deleteModalLabel">Delete Project?</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          Are you sure you want to delete project <span ref="prompt_project_name" style="font-weight: bold;"></span>?
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" ref="Close" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="deleteProjectClick">Delete Project</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { Modal } from "bootstrap"
import profiles from '../data/profiles.json'

export default {
  name: "Projects.vue",
  data() {
    return {
      selectedProject: null,
      project_name: null,
      project_fps_num: 30,
      project_fps_den: 1,
      project_width: 1920,
      project_height: 1080,
      editing: false,
      show_spinner: false,
      selected_profile: 3031,
      profiles,
      project_archive_file: null,
      creating_project: false,
      load_archive_enabled: false
    }
  },
  methods: {
    profileChanged() {
      let profile_details = this.profiles.filter(p => p.id == this.selected_profile)[0]
      this.project_width = profile_details.width
      this.project_height = profile_details.height
      this.project_fps_num = profile_details.fps_num
      this.project_fps_den = profile_details.fps_den
    },
    launchEditor(project) {
      this.clearErrors()
      this.$router.push(`/projects/${project.id}`)
    },
    editProjectClick(project) {
      this.clearErrors()
      this.selectedProject = project
      this.editing = true
      this.disableArchiveSection()

      // Set profile to match current project settings
      let profile_details = this.profiles.filter(p => p.width == project.width &&
                                                      p.height == project.height &&
                                                      p.fps_num == project.fps_num &&
                                                      p.fps_den == project.fps_den)
      if (profile_details.length > 0) {
        this.selected_profile = profile_details[0].id
      }
      this.project_name = project.name
      this.project_width = project.width
      this.project_height = project.height
      this.project_fps_num = project.fps_num
      this.project_fps_den = project.fps_den
      let myModal = new Modal(this.$refs.newProjectModal)
      myModal.show()
    },
    showNewProjectModal() {
      this.selectedProject = null
      this.editing = false
      this.project_name = 'New Project'
      this.project_fps_num = 30
      this.project_fps_den = 1
      this.project_width = 1920
      this.project_height = 1080
      this.selected_profile = 3031
      this.disableArchiveSection()

      let myModal = new Modal(this.$refs.newProjectModal)
      myModal.show()
    },
    hideNewProjectModal() {
      if (!this.$refs.newProjectModal) {
        return
      }
      let myModal = Modal.getInstance(this.$refs.newProjectModal)
      if (!myModal) {
        myModal = new Modal(this.$refs.newProjectModal)
      }
      myModal.hide()
    },
    async createProjectClick() {
      if (this.creating_project) {
        return
      }
      this.creating_project = true
      let payload = {
        "name": this.project_name,
        "width": this.project_width,
        "height": this.project_height,
        "fps_num": this.project_fps_num,
        "fps_den": this.project_fps_den,
        "sample_rate": 44100,
        "channels": 2,
        "channel_layout": 3,
        "json": {}
      }

      try {
        let project = await this.createProject(payload)
        if (project && project.id) {
          await this.maybeLoadArchive(project.id)
          this.hideNewProjectModal()
          this.$router.push(`/projects/${project.id}`)
        } else if (!project) {
          this.clearArchiveSelection()
        }
      } finally {
        this.creating_project = false
      }
    },
    triggerArchiveBrowse() {
      if (this.$refs.projectArchiveInput) {
        this.$refs.projectArchiveInput.click()
      }
    },
    archiveSwitchChanged() {
      if (!this.load_archive_enabled) {
        this.clearArchiveSelection()
      }
    },
    disableArchiveSection() {
      this.load_archive_enabled = false
      this.clearArchiveSelection()
    },
    onArchiveSelected(event) {
      const files = event?.target?.files
      if (!files || files.length === 0) {
        this.project_archive_file = null
        return
      }
      const file = files[0]
      const fileName = file.name?.toLowerCase() || ''
      if (!fileName.endsWith('.zip') && !fileName.endsWith('.osp')) {
        this.clearArchiveSelection()
        return
      }
      this.project_archive_file = file
    },
    clearArchiveSelection() {
      this.project_archive_file = null
      if (this.$refs.projectArchiveInput) {
        this.$refs.projectArchiveInput.value = null
      }
    },
    async maybeLoadArchive(projectId) {
      if (!this.load_archive_enabled || !this.project_archive_file) {
        return
      }
      try {
        await this.loadProjectArchive({ projectId, file: this.project_archive_file })
      } catch(err) {
        console.error('Failed to load project archive', err)
      } finally {
        this.clearArchiveSelection()
        this.load_archive_enabled = false
      }
    },
    async updateProjectClick() {
      this.selectedProject.name = this.project_name
      this.selectedProject.width = this.project_width
      this.selectedProject.height = this.project_height
      this.selectedProject.fps_num = this.project_fps_num
      this.selectedProject.fps_den = this.project_fps_den

      await this.updateProject(this.selectedProject)
      this.$refs.CloseProjectModal.click();
    },
    showDeletePrompt(project) {
      this.selectedProject = project;
      this.$refs.prompt_project_name.innerText = this.selectedProject.name
      let myModal = new Modal(this.$refs.deleteModal)
      myModal.show()
    },
    deleteProjectClick() {
      this.deleteProject(this.selectedProject.id)
      this.$refs.Close.click();
      this.selectedProject = null
    },
    ...mapActions(['loadProjects', 'createProject', 'deleteProject', 'updateProject', 'loadProjectArchive']),
    ...mapMutations(['setProject', 'clearErrors'])
  },
  computed: {
    ...mapGetters(['thumbnailedProjects', 'isAuthenticated']),
  },
  async mounted() {
    console.log("Projects component mounted")
    this.show_spinner = true
    if (this.isAuthenticated) {
      await this.loadProjects()
    }
    this.show_spinner = false
  }
}
</script>

<style scoped>
  button {
    margin-left: 5px;
  }
  .project-spinner {
    width: 3em!important;
    height: 3em!important;
  }
  .context-menu {
    color: #ffffff;
    position: absolute;
    top: 0%;
    right: 0%;
    padding-top: 10px;
    padding-right: 10px;
    cursor: pointer;
  }
  .context-menu:hover {
    color: #0d6efd;
  }
  .img-parent {
    position: relative;
  }
  .project-thumbnail {
    cursor: pointer;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .profile-label {
    color: #ffffff;
    position: absolute;
    bottom: 5%;
    z-index: 999;
    right: 5%;
    font-size: .8em;
    background-color: #000000;
    border-radius: 4px;
    padding: 3px;
    opacity: 0.75;
  }
  .project-card {
    height: 184px;
  }
  .project-badge {
    position: absolute;
    bottom: 5%;
    left: 5%;
    padding: 8px;
    opacity: 0.75;
    background-color: #000000;
    border-radius: 4px;
  }
  .small-dropdown {
    min-width: 5em;
    opacity: 0.8;
  }
  .archive-file-name {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .create-btn-spinner {
    width: 0.9rem;
    height: 0.9rem;
    border-width: 0.12em;
  }
</style>
